import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { API_URL } from '../demoMode';
import { ScreenShell } from './ScreenShared';

async function parseApiResponse(response) {
  const contentType = response.headers.get('content-type') || '';
  const rawText = await response.text();
  const looksJson = contentType.includes('application/json') || /^\s*[[{]/.test(rawText);

  if (!looksJson) {
    return { payload: null, rawText };
  }

  try {
    return { payload: JSON.parse(rawText), rawText };
  } catch {
    return { payload: null, rawText };
  }
}

function Register({ onLogin }) {
  const [form, setForm] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [ageConfirmed, setAgeConfirmed] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!ageConfirmed) {
      setError('You must confirm that you are 18 years of age or older to create an account.');
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      const registerResponse = await fetch(`${API_URL}/v1/user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: form.name.trim(),
          username: form.username.trim().toLowerCase(),
          email: form.email.trim(),
          password: form.password
        })
      });

      const { payload: registerPayload, rawText: registerText } = await parseApiResponse(registerResponse);
      if (!registerResponse.ok) {
        throw new Error(registerPayload?.detail || `Unable to create account (HTTP ${registerResponse.status}). ${registerText ? 'Check API/proxy routing.' : ''}`);
      }

      const loginForm = new URLSearchParams();
      loginForm.append('username', form.username.trim().toLowerCase());
      loginForm.append('password', form.password);

      const loginResponse = await fetch(`${API_URL}/v1/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: loginForm,
        credentials: 'include'
      });

      const { payload: loginPayload, rawText: loginText } = await parseApiResponse(loginResponse);
      if (!loginResponse.ok) {
        throw new Error(loginPayload?.detail || `Account created, but auto-login failed (HTTP ${loginResponse.status}). ${loginText ? 'Check API/proxy routing.' : ''}`);
      }

      if (!loginPayload?.access_token) {
        throw new Error('Account created, but login response did not include an access token.');
      }

      onLogin(loginPayload.access_token);
    } catch (err) {
      setError(err.message || 'Unable to create account.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScreenShell>
      <p className="figma-brand">INSTANT CONNECTIONS</p>
      <h2 className="figma-section-title">Create account</h2>
      <p className="figma-sub">Join with a real account to start connecting.</p>

      <div className="figma-age-gate-card">
        <div className="figma-age-gate-header">
          <div className="figma-age-gate-icon">
            <ShieldCheck size={22} className="figma-age-icon" />
          </div>
          <div>
            <h3 className="figma-age-gate-title">Age Verification</h3>
            <p className="figma-age-gate-desc">
              Instant Connections is an adult platform. You must be 18 or older to continue.
            </p>
          </div>
        </div>
        <label className="figma-checkline figma-age-checkline" htmlFor="ageConfirmed">
          <input
            id="ageConfirmed"
            type="checkbox"
            checked={ageConfirmed}
            onChange={(e) => setAgeConfirmed(e.target.checked)}
          />
          <span>
            I confirm that I am <strong>18 years of age or older</strong> and agree to enter this platform.
          </span>
        </label>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit} className="figma-form">
        <label htmlFor="name">
          Name
          <input
            id="name"
            type="text"
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            placeholder="Jane Doe"
            required
            disabled={loading}
          />
        </label>

        <label htmlFor="username">
          Username
          <input
            id="username"
            type="text"
            value={form.username}
            onChange={(e) => setField('username', e.target.value)}
            placeholder="jane"
            pattern="[a-z0-9]+"
            title="Only lowercase letters and numbers are allowed"
            required
            disabled={loading}
          />
        </label>

        <label htmlFor="email">
          Email
          <input
            id="email"
            type="email"
            value={form.email}
            onChange={(e) => setField('email', e.target.value)}
            placeholder="jane@example.com"
            required
            disabled={loading}
          />
        </label>

        <label htmlFor="password">
          Password
          <input
            id="password"
            type="password"
            value={form.password}
            onChange={(e) => setField('password', e.target.value)}
            placeholder="At least 8 characters"
            minLength={8}
            required
            disabled={loading}
          />
        </label>

        <label htmlFor="confirmPassword">
          Confirm Password
          <input
            id="confirmPassword"
            type="password"
            value={form.confirmPassword}
            onChange={(e) => setField('confirmPassword', e.target.value)}
            placeholder="Repeat password"
            minLength={8}
            required
            disabled={loading}
          />
        </label>

        <button type="submit" className="figma-btn figma-btn-primary" disabled={loading}>
          {loading ? 'Creating account...' : 'Create account'}
        </button>
      </form>

      <Link to="/login" className="figma-btn figma-btn-secondary">
        Back to login
      </Link>
    </ScreenShell>
  );
}

export default Register;
